import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  variant: ToastVariant;
  title: string;
  message?: string;
  onClose: () => void;
  duration?: number;
}

export const Toast: React.FC<ToastProps> = ({ 
  variant, 
  title, 
  message, 
  onClose,
  duration = 5000 
}) => {
  const config = {
    success: {
      icon: <CheckCircle size={16} className="text-green-600" />,
      iconBg: 'bg-green-100',
    },
    error: {
      icon: <XCircle size={16} className="text-red-600" />,
      iconBg: 'bg-red-100',
    },
    warning: {
      icon: <AlertCircle size={16} className="text-amber-600" />,
      iconBg: 'bg-amber-100',
    },
    info: {
      icon: <Info size={16} className="text-blue-600" />,
      iconBg: 'bg-blue-100',
    },
  };

  const { icon, iconBg } = config[variant];

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  return (
    <div className="fixed bottom-6 right-6 z-50 bg-white rounded-lg shadow-xl ring-1 ring-neutral-200 p-4 flex items-start gap-3 max-w-sm animate-slide-in-right">
      <div className={`w-8 h-8 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-neutral-900">{title}</p>
        {message && (
          <p className="text-xs text-neutral-600 mt-1">{message}</p>
        )}
      </div>
      <button 
        onClick={onClose}
        className="p-1 hover:bg-neutral-100 rounded-md transition-colors"
      >
        <X size={16} className="text-neutral-600" />
      </button>
    </div>
  );
};

// Toast Container for managing multiple toasts
interface ToastContainerProps {
  toasts: Array<Omit<ToastProps, 'onClose'> & { id: string }>;
  onRemove: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  return (
    <div className="fixed bottom-6 right-6 z-50 space-y-3">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={() => onRemove(toast.id)}
        />
      ))}
    </div>
  );
};
